import { supertypeClass, property } from '@haventech/amorphic';
import { Customer } from '../../../../common/js/customer/Customer';
import { CPQuoteRateClassDescriptions } from '../../../js/interfaces/CoverpathTypes';
import { RateClasses } from '../../../../common/js/static/RateClasses';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import { ClientBaseQuote } from '../ClientBaseQuote';
import { Product } from '../../../../common/js/static/Product';

@supertypeClass({ toClient: true, toServer: true })
export class CoverpathTermQuote extends ClientBaseQuote {
  @property({
    rule: ['numeric', 'required'],
    values: [10, 15, 20, 25, 30],
    descriptions: {
      '10': '10 years',
      '15': '15 years',
      '20': '20 years',
      '25': '25 years',
      '30': '30 years',
    },
    compositeValidators: ['termQuoteHasValidTerm'],
  })
  term: number;

  @property({
    isLocal: true,
    rule: ['numeric', 'required'],
    values: ['10', '15', '20', '25', '30'],
    descriptions: {
      '10': '10',
      '15': '15',
      '20': '20',
      '25': '25',
      '30': '30',
    },
    compositeValidators: ['termQuoteHasValidTerm'],
  })
  set clientTermDisplay(value: number) {
    this.term = value;
  }

  get clientTermDisplay(): number {
    return this.term;
  }

  @property({
    values: { '0': 'excellent health', '1': 'very good health', '2': 'good health', '3': 'average health' },
  })
  healthClassNumber: number;

  healthClassNumberTrigger() {
    this.healthClass = CoverpathTermQuote.calculateHealthClassCode(this.healthClassNumber, this.smoker);
  }

  @property({ values: { false: 'do not', true: 'do' } })
  smoker: boolean;

  @property({
    comment: 'product code',
  })
  product: string = 'CASTerm';

  @property({
    comment: 'the type of product, term',
  })
  productType: 'Whole' | 'Term' = 'Term';

  @property({ isLocal: true })
  _hasEcp: boolean = false;

  @property({
    isLocal: true,
    values: [true, false],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'mark this term quote to have ECP',
  })
  set hasEcp(value: boolean) {
    this._hasEcp = value;
    this.product = this._hasEcp ? Product.products.CASTermECP.code : Product.products.CASTerm.code;
  }

  get hasEcp(): boolean {
    if (this.product === Product.products.CASTermECP.code) {
      this._hasEcp = true;
    } else {
      this._hasEcp = false;
    }
    return this._hasEcp;
  }

  @property({
    values: Object.keys(CPQuoteRateClassDescriptions),
    descriptions: CoverpathUtils.getRateClassDescriptions('longDescription'),
  })
  healthClass: string;

  healthClassTrigger() {
    this.smoker = RateClasses[this.healthClass].smoker;
  }

  @property({
    rule: ['DOB', 'required'],
    validate: `isActualAgeWithin(${Product.productSpecs.AllProductAge.minTermAge}, ${Product.productSpecs.AllProductAge.maxAge})`,
    sensitiveData: true,
    comment: 'Date of Birth of the Individual/Person',
  })
  dateOfBirth: Date;

  @property({
    comment: `amount of death benefit an individual can receive`,
    rule: ['currency', 'required'],
    compositeValidators: ['termQuoteHasValidFaceAmount'],
  })
  faceAmount: number;

  @property({
    toServer: false,
    rule: 'required',
  })
  ownerEqualsInsured: boolean;

  @property({
    rule: 'required',
  })
  carrierCode: string;

  @property({
    comment: `if agent start the draft application`,
  })
  isAgentAssistant: boolean;

  @property({
    comment: 'designates whether the agent or the client created the quote',
  })
  isAdvisorQuote: boolean;

  @property({
    comment: `Payment option selected by user`,
    values: ['Annual', 'Monthly'],
    descriptions: {
      Annual: 'Yearly',
      Monthly: 'Monthly',
    },
  })
  paymentFrequency: string = 'Monthly';

  @property({ comment: 'Flag that notes if the premium should be shown to client or not' })
  showPremiumToClient: boolean;

  static new(customer: Customer) {
    return new CoverpathTermQuote().init(customer);
  }

  init(customer: Customer): CoverpathTermQuote {
    super.init(customer);
    return this;
  }

  copyFromCoverpathTermQuote(quote: CoverpathTermQuote) {
    this.gender = quote.gender;
    this.dateOfBirth = quote.dateOfBirth;
    this.smoker = RateClasses.findByStringCode(quote.healthClass).smoker;
    this.healthClassNumber = CPQuoteRateClassDescriptions[quote.healthClass].healthClassNumber;
    this.contractState = quote.contractState;
    this.term = quote.term;
    this.faceAmount = quote.faceAmount;
    this.healthClass = quote.healthClass;
    this.waiverOfPremium = quote.waiverOfPremium;
    this.waiverOfPremiumAmount = quote.waiverOfPremiumAmount;
    this.isAgentAssistant = quote.isAgentAssistant;
    this.carrierCode = quote.carrierCode;
    this.paymentFrequency = quote.paymentFrequency;
    this.hasEcp = quote.hasEcp;
  }

  verifyProperties(): boolean {
    return (
      !!this.contractState &&
      this.__values__('contractState').indexOf(this.contractState) > -1 &&
      !!this.dateOfBirth &&
      !!this.gender &&
      !!this.healthClass &&
      !!this.faceAmount &&
      !!this.term &&
      !!this.paymentFrequency
    );
  }
}
